import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import AuthModal from '@/components/feature/AuthModal';

export default function HeroSection() {
  const [visible, setVisible] = useState(false);
  const [authOpen, setAuthOpen] = useState(false);
  const [authTab, setAuthTab] = useState<'login' | 'register'>('login');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const t = setTimeout(() => setVisible(true), 100);
    return () => clearTimeout(t);
  }, []);

  const navLinks = [
    { path: '/', label: 'Home' },
    { path: '/about', label: 'About Us' },
    { path: '/products', label: 'Shop' },
    { path: '/investors', label: 'Our Process' },
    { path: '/contact', label: 'Contact' },
  ];

  const isActive = (path: string) => location.pathname === path;

  return (
    <section className="flex flex-col" style={{ background: '#faf7f2' }}>

      {/* ── NAVBAR — scrolls with page ── */}
      <header className="relative z-50 bg-white border-b border-stone-100">
        <div className="w-full px-4 sm:px-6 lg:px-16 xl:px-24">
          <div className="flex flex-col items-center pt-4 pb-2">

            {/* Logo centered — circular */}
            <Link to="/" className="mb-2">
              <div
                className="w-28 h-28 md:w-32 md:h-32 rounded-full overflow-hidden flex items-center justify-center"
                style={{ border: '2px solid #e8d5b7', background: '#fff' }}
              >
                <img
                  src="https://static.readdy.ai/image/3301efb4abc894ce1097fc48d2d46708/299f592c2e3e9cf1636e28f1b3d51d14.png"
                  alt="Faasley Foods"
                  className="w-full h-full object-contain"
                />
              </div>
            </Link>

            {/* Nav links row */}
            <div className="hidden md:flex items-center justify-center w-full relative pb-1">
              <div className="flex items-center gap-8">
                {navLinks.map((link) => (
                  <Link
                    key={link.path}
                    to={link.path}
                    className={`text-sm font-medium transition-colors whitespace-nowrap ${
                      isActive(link.path)
                        ? 'text-amber-700 font-semibold'
                        : 'text-stone-600 hover:text-stone-900'
                    }`}
                  >
                    {link.label}
                  </Link>
                ))}
              </div>

              {/* Right icons */}
              <div className="absolute right-0 flex items-center gap-2">
                <button className="w-8 h-8 flex items-center justify-center text-stone-500 hover:text-stone-900 transition-colors cursor-pointer">
                  <i className="ri-search-line text-base"></i>
                </button>
                <button
                  onClick={() => { setAuthTab('login'); setAuthOpen(true); }}
                  className="w-8 h-8 flex items-center justify-center text-stone-500 hover:text-stone-900 transition-colors cursor-pointer"
                >
                  <i className="ri-user-line text-base"></i>
                </button>
                <Link
                  to="/products"
                  className="w-8 h-8 flex items-center justify-center text-stone-500 hover:text-stone-900 transition-colors"
                >
                  <i className="ri-shopping-cart-line text-base"></i>
                </Link>
              </div>
            </div>

            {/* Mobile toggle */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="md:hidden text-stone-700 cursor-pointer mt-1"
            >
              <i className={`ri-${mobileMenuOpen ? 'close' : 'menu-3'}-line text-xl`}></i>
            </button>
          </div>
        </div>

        {/* Mobile menu */}
        {mobileMenuOpen && (
          <div className="md:hidden bg-white border-t border-stone-100 px-4 py-4 space-y-1">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                onClick={() => setMobileMenuOpen(false)}
                className={`block py-2.5 px-4 rounded-xl text-sm font-medium transition-colors ${
                  isActive(link.path) ? 'text-amber-700 bg-amber-50' : 'text-stone-700 hover:bg-stone-50'
                }`}
              >
                {link.label}
              </Link>
            ))}
            <div className="pt-2 flex gap-2">
              <button
                onClick={() => { setMobileMenuOpen(false); setAuthTab('login'); setAuthOpen(true); }}
                className="flex-1 py-2.5 px-4 rounded-xl text-sm font-medium text-stone-700 border border-stone-200 hover:bg-stone-50 transition-colors cursor-pointer whitespace-nowrap"
              >
                Sign In
              </button>
              <Link
                to="/products"
                onClick={() => setMobileMenuOpen(false)}
                className="flex-1 py-2.5 px-4 rounded-xl text-sm font-semibold text-white bg-stone-800 hover:bg-stone-700 transition-colors text-center whitespace-nowrap"
              >
                Shop Now
              </Link>
            </div>
          </div>
        )}
      </header>

      {/* ── HERO — left scene + right Kamini overlay ── */}
      <div style={{ background: '#faf7f2' }}>
        <div className="relative w-full overflow-hidden">
          
          {/* Base scene image */}
          <img
            src="https://static.readdy.ai/image/3301efb4abc894ce1097fc48d2d46708/7022bd7bf866cd54e26f472c20f2495a.png"
            alt="Faasley Foods"
            className="w-full block"
            style={{ filter: 'contrast(1.12) saturate(1.25) brightness(1.04)' }}
          />

          {/* Kamini overlay — covers the right side lady */}
          <div 
            className="absolute top-0 right-0 bottom-0 pointer-events-none"
            style={{ width: '45%' }}
          >
            <img
              src="https://storage.readdy-site.link/project_files/c44a33b9-73a7-4f29-8c86-80a04dae53c1/5cb346ab-a7f1-4cf6-953f-cf38d742223f_Kamini.jpg?v=7ff1f630b615292716d05c68d8042a2b"
              alt="Kamini Thakur - Founder"
              className="w-full h-full object-cover object-top"
              style={{ objectPosition: 'center 15%' }}
            />
            {/* Left edge blend — wide, multi-stop for ultra-smooth merge */}
            <div 
              className="absolute inset-y-0 left-0 w-40 pointer-events-none"
              style={{ background: 'linear-gradient(to right, rgba(250,247,242,1) 0%, rgba(250,247,242,0.85) 25%, rgba(250,247,242,0.5) 55%, rgba(250,247,242,0.15) 80%, transparent 100%)' }}
            ></div>
            {/* Top fade */}
            <div 
              className="absolute top-0 left-0 right-0 h-16 pointer-events-none"
              style={{ background: 'linear-gradient(to bottom, rgba(250,247,242,0.6) 0%, transparent 100%)' }}
            ></div>
            {/* Bottom fade */}
            <div 
              className="absolute bottom-0 left-0 right-0 h-28 pointer-events-none"
              style={{ background: 'linear-gradient(to top, rgba(250,247,242,1) 0%, rgba(250,247,242,0.7) 40%, transparent 100%)' }}
            ></div>
          </div>

          {/* Bottom fade into cream */}
          <div className="absolute bottom-0 left-0 right-0 h-16 pointer-events-none" style={{ background: 'linear-gradient(to top, #faf7f2, transparent)' }}></div>
        </div>
      </div>

      {/* ── TAGLINE SECTION — cream background ── */}
      <div
        className="w-full text-center py-16 md:py-20 px-4 relative overflow-hidden"
        style={{ background: 'linear-gradient(to bottom, #faf7f2, #f5ede0)' }}
      >
        {/* Decorative mandala watermark */}
        <div
          className="absolute bottom-0 right-0 w-64 h-64 opacity-10 pointer-events-none"
          style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 200 200'%3E%3Ccircle cx='100' cy='100' r='90' fill='none' stroke='%23c9a96e' stroke-width='2'/%3E%3Ccircle cx='100' cy='100' r='70' fill='none' stroke='%23c9a96e' stroke-width='1.5'/%3E%3Ccircle cx='100' cy='100' r='50' fill='none' stroke='%23c9a96e' stroke-width='1'/%3E%3C/svg%3E")`,
            backgroundSize: 'contain',
            backgroundRepeat: 'no-repeat',
          }}
        ></div>

        {/* Decorative divider top */}
        <div className="flex items-center justify-center gap-3 mb-8">
          <div className="h-px w-16 bg-amber-300"></div>
          <i className="ri-flower-line text-amber-400 text-lg"></i>
          <div className="h-px w-16 bg-amber-300"></div>
        </div>

        <h2
          className="font-bold mb-6 leading-snug"
          style={{ fontFamily: "'Playfair Display', serif", fontSize: 'clamp(1.6rem, 3vw, 2.4rem)', color: '#2c1a0e' }}
        >
          Quality Products,<br />Handcrafted with Love &amp; Care
        </h2>

        <Link
          to="/products"
          className="inline-flex items-center justify-center font-semibold px-8 py-3.5 rounded-md transition-all whitespace-nowrap text-sm"
          style={{ background: '#3b2010', color: '#fff' }}
        >
          Explore Our Collection
        </Link>
      </div>

      <AuthModal
        isOpen={authOpen}
        onClose={() => setAuthOpen(false)}
        defaultTab={authTab}
      />
    </section>
  );
}
